import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";
import swal from "sweetalert";
import http from "../../../../utils/http-common";
import { meCan } from "../../../../utils/adminPermissions";

const EMPTY_KEYS = {
  stripeSecretKey: "",
  stripePublicKey: "",
  stripeWebhookSecret: "",
  yousignApiKey: "",
  yousignWebhookSecret: "",
  yousignEnv: "sandbox",
  dailyApiKey: "",
  awsAccessKeyId: "",
  awsSecretAccessKey: "",
  awsChimeRegion: "eu-west-1",
  calendlyToken: "",
  googleCalendarClientId: "",
  googleCalendarClientSecret: "",
  googleCalendarRefreshToken: "",
};

function ConfiguredBadge({ configured }) {
  return (
    <Badge color={configured ? "success" : "secondary"} className="ml-2">
      {configured ? "Configured" : "Not configured"}
    </Badge>
  );
}

function HiddenUsernameForPasswordForm({ id }) {
  return (
    <input
      id={id}
      type="text"
      name={`${id}-username`}
      autoComplete="username"
      tabIndex={-1}
      aria-hidden="true"
      readOnly
      defaultValue=""
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    />
  );
}

function PasswordField({ id, name, value, onChange, placeholder, disabled }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="form-control-wrap">
      <a
        href="#toggle"
        onClick={(e) => {
          e.preventDefault();
          setVisible((v) => !v);
        }}
        className="form-icon form-icon-right passcode-switch"
        data-target={id}
      >
        <em className={`passcode-icon icon-show icon ni ni-eye${visible ? "-off" : ""}`}></em>
      </a>
      <Input
        type={visible ? "text" : "password"}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || "Leave blank to keep current value"}
        disabled={disabled}
        className="form-control-lg"
        autoComplete="new-password"
      />
    </div>
  );
}

/**
 * Canonical `integrations.*` secrets (GET/PUT /api/v1/settings/api-keys).
 * @param {{ onSaved?: () => void }} props
 */
export default function IntegrationsApiKeysPanel({ onSaved }) {
  const [masked, setMasked] = useState({});
  const [form, setForm] = useState(EMPTY_KEYS);
  const [saving, setSaving] = useState({});
  const [loadingKeys, setLoadingKeys] = useState(true);
  const canEditKeys = meCan("technique.integrations.act");
  const keysLocked = !canEditKeys;

  const fetchMasked = useCallback(async () => {
    try {
      setLoadingKeys(true);
      const res = await http.get("/api/v1/settings/api-keys");
      setMasked(res.data?.data || {});
    } catch {
      swal("Error", "Failed to load API keys status", "error");
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    fetchMasked();
  }, [fetchMasked]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveGroup = async (groupKey, fields) => {
    if (!canEditKeys) {
      swal("Access denied", "You do not have permission to update API keys.", "error");
      return;
    }
    const payload = {};
    fields.forEach((f) => {
      if (form[f] !== undefined && form[f] !== "") {
        payload[`integrations.${f}`] = form[f];
      }
    });

    if (Object.keys(payload).length === 0) {
      swal("Info", "No changes to save (all fields are blank).", "info");
      return;
    }

    try {
      setSaving((prev) => ({ ...prev, [groupKey]: true }));
      const res = await http.put("/api/v1/settings/api-keys", payload);
      setMasked(res.data?.data || {});
      setForm((prev) => {
        const cleared = { ...prev };
        fields.forEach((f) => {
          cleared[f] = "";
        });
        return cleared;
      });
      swal("Saved", "API keys updated successfully.", "success");
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to save API keys.", "error");
    } finally {
      setSaving((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const isConfigured = (key) => masked[`integrations.${key}`]?.configured;
  const maskedVal = (key) => masked[`integrations.${key}`]?.masked || "";

  return (
    <div>
      <h6 className="overline-title text-primary-alt mb-3">API keys (integrations.*)</h6>
      <p className="text-soft small mb-4">
        Preferred storage for third-party credentials. The backend reads these before <code>process.env</code> fallbacks.
      </p>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Stripe</h5>
            <ConfiguredBadge
              configured={
                isConfigured("stripeSecretKey") &&
                isConfigured("stripePublicKey") &&
                isConfigured("stripeWebhookSecret")
              }
            />
          </div>
          <p className="text-soft mb-3">Required for teleconsultation payments.</p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <HiddenUsernameForPasswordForm id="integrations-apikeys-stripe-a11y" />
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Secret Key{" "}
                      {maskedVal("stripeSecretKey") && (
                        <span className="text-soft ml-1">({maskedVal("stripeSecretKey")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="stripeSecretKey"
                      name="stripeSecretKey"
                      value={form.stripeSecretKey}
                      onChange={handleChange}
                      disabled={keysLocked || saving.stripe}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Public Key{" "}
                      {maskedVal("stripePublicKey") && (
                        <span className="text-soft ml-1">({maskedVal("stripePublicKey")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="stripePublicKey"
                      name="stripePublicKey"
                      value={form.stripePublicKey}
                      onChange={handleChange}
                      disabled={keysLocked || saving.stripe}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Webhook Secret{" "}
                      {maskedVal("stripeWebhookSecret") && (
                        <span className="text-soft ml-1">({maskedVal("stripeWebhookSecret")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="stripeWebhookSecret"
                      name="stripeWebhookSecret"
                      value={form.stripeWebhookSecret}
                      onChange={handleChange}
                      disabled={keysLocked || saving.stripe}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.stripe}
                    onClick={() =>
                      saveGroup("stripe", ["stripeSecretKey", "stripePublicKey", "stripeWebhookSecret"])
                    }
                  >
                    {saving.stripe ? "Saving…" : "Save Stripe keys"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Yousign (Electronic Signature)</h5>
            <ConfiguredBadge configured={isConfigured("yousignApiKey")} />
          </div>
          <p className="text-soft mb-3">Required for prescription electronic signatures.</p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <HiddenUsernameForPasswordForm id="integrations-apikeys-yousign-a11y" />
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      API Key{" "}
                      {maskedVal("yousignApiKey") && (
                        <span className="text-soft ml-1">({maskedVal("yousignApiKey")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="yousignApiKey"
                      name="yousignApiKey"
                      value={form.yousignApiKey}
                      onChange={handleChange}
                      disabled={keysLocked || saving.yousign}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Webhook Secret{" "}
                      {maskedVal("yousignWebhookSecret") && (
                        <span className="text-soft ml-1">({maskedVal("yousignWebhookSecret")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="yousignWebhookSecret"
                      name="yousignWebhookSecret"
                      value={form.yousignWebhookSecret}
                      onChange={handleChange}
                      disabled={keysLocked || saving.yousign}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Environment</Label>
                    <Input
                      type="select"
                      id="yousignEnv"
                      name="yousignEnv"
                      value={form.yousignEnv}
                      onChange={handleChange}
                      disabled={keysLocked || saving.yousign}
                    >
                      <option value="sandbox">Sandbox (testing)</option>
                      <option value="production">Production</option>
                    </Input>
                    {maskedVal("yousignEnv") && (
                      <span className="form-note">
                        Current: <strong>{maskedVal("yousignEnv")}</strong>
                      </span>
                    )}
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.yousign}
                    onClick={() =>
                      saveGroup("yousign", ["yousignApiKey", "yousignWebhookSecret", "yousignEnv"])
                    }
                  >
                    {saving.yousign ? "Saving…" : "Save Yousign keys"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Video — Amazon Chime SDK</h5>
            <ConfiguredBadge
              configured={
                isConfigured("awsAccessKeyId") &&
                isConfigured("awsSecretAccessKey") &&
                isConfigured("awsChimeRegion")
              }
            />
          </div>
          <p className="text-soft mb-3">
            HIPAA-eligible video consultations (replaces Daily.co). Region is the Chime media region (e.g. eu-west-1).
          </p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <HiddenUsernameForPasswordForm id="integrations-apikeys-chime-a11y" />
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      AWS Access Key ID{" "}
                      {maskedVal("awsAccessKeyId") && (
                        <span className="text-soft ml-1">({maskedVal("awsAccessKeyId")})</span>
                      )}
                    </Label>
                    <Input
                      type="text"
                      id="awsAccessKeyId"
                      name="awsAccessKeyId"
                      value={form.awsAccessKeyId}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current value"
                      disabled={keysLocked || saving.chime}
                      className="form-control-lg"
                      autoComplete="off"
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      AWS Secret Access Key{" "}
                      {maskedVal("awsSecretAccessKey") && (
                        <span className="text-soft ml-1">({maskedVal("awsSecretAccessKey")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="awsSecretAccessKey"
                      name="awsSecretAccessKey"
                      value={form.awsSecretAccessKey}
                      onChange={handleChange}
                      disabled={keysLocked || saving.chime}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      AWS Chime Region{" "}
                      {maskedVal("awsChimeRegion") && (
                        <span className="text-soft ml-1">({maskedVal("awsChimeRegion")})</span>
                      )}
                    </Label>
                    <Input
                      type="text"
                      id="awsChimeRegion"
                      name="awsChimeRegion"
                      value={form.awsChimeRegion}
                      onChange={handleChange}
                      placeholder="eu-west-1"
                      disabled={keysLocked || saving.chime}
                      className="form-control-lg"
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.chime}
                    onClick={() =>
                      saveGroup("chime", ["awsAccessKeyId", "awsSecretAccessKey", "awsChimeRegion"])
                    }
                  >
                    {saving.chime ? "Saving…" : "Save Chime credentials"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Video (Daily.co — legacy)</h5>
            <ConfiguredBadge configured={isConfigured("dailyApiKey")} />
          </div>
          <p className="text-soft mb-3">Legacy Daily.co key (no longer used for new consultations).</p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <HiddenUsernameForPasswordForm id="integrations-apikeys-daily-a11y" />
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Daily API Key{" "}
                      {maskedVal("dailyApiKey") && (
                        <span className="text-soft ml-1">({maskedVal("dailyApiKey")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="dailyApiKey"
                      name="dailyApiKey"
                      value={form.dailyApiKey}
                      onChange={handleChange}
                      disabled={keysLocked || saving.daily}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.daily}
                    onClick={() => saveGroup("daily", ["dailyApiKey"])}
                  >
                    {saving.daily ? "Saving…" : "Save Daily.co key"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Calendly</h5>
            <ConfiguredBadge configured={isConfigured("calendlyToken")} />
          </div>
          <p className="text-soft mb-3">Global default token. Clinics may use their own in calendar sync.</p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Access Token{" "}
                      {maskedVal("calendlyToken") && (
                        <span className="text-soft ml-1">({maskedVal("calendlyToken")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="calendlyToken"
                      name="calendlyToken"
                      value={form.calendlyToken}
                      onChange={handleChange}
                      disabled={keysLocked || saving.calendly}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.calendly}
                    onClick={() => saveGroup("calendly", ["calendlyToken"])}
                  >
                    {saving.calendly ? "Saving…" : "Save Calendly token"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <div className="d-flex align-items-center mb-3">
            <h5 className="card-title mb-0">Google Calendar (OAuth app)</h5>
            <ConfiguredBadge
              configured={
                isConfigured("googleCalendarClientId") &&
                isConfigured("googleCalendarClientSecret") &&
                isConfigured("googleCalendarRefreshToken")
              }
            />
          </div>
          <p className="text-soft mb-3">
            Server OAuth app for Google Calendar sync. Per-clinic refresh tokens are stored when partners connect.
          </p>
          {loadingKeys ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
            </div>
          ) : (
            <Form>
              <HiddenUsernameForPasswordForm id="integrations-apikeys-googlecal-a11y" />
              <Row className="g-3">
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Client ID{" "}
                      {maskedVal("googleCalendarClientId") && (
                        <span className="text-soft ml-1">({maskedVal("googleCalendarClientId")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="googleCalendarClientId"
                      name="googleCalendarClientId"
                      value={form.googleCalendarClientId}
                      onChange={handleChange}
                      disabled={keysLocked || saving.google}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Client Secret{" "}
                      {maskedVal("googleCalendarClientSecret") && (
                        <span className="text-soft ml-1">({maskedVal("googleCalendarClientSecret")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="googleCalendarClientSecret"
                      name="googleCalendarClientSecret"
                      value={form.googleCalendarClientSecret}
                      onChange={handleChange}
                      disabled={keysLocked || saving.google}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>
                      Refresh token (server / Playground){" "}
                      {maskedVal("googleCalendarRefreshToken") && (
                        <span className="text-soft ml-1">({maskedVal("googleCalendarRefreshToken")})</span>
                      )}
                    </Label>
                    <PasswordField
                      id="googleCalendarRefreshToken"
                      name="googleCalendarRefreshToken"
                      value={form.googleCalendarRefreshToken}
                      onChange={handleChange}
                      disabled={keysLocked || saving.google}
                    />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={keysLocked || saving.google}
                    onClick={() =>
                      saveGroup("google", [
                        "googleCalendarClientId",
                        "googleCalendarClientSecret",
                        "googleCalendarRefreshToken",
                      ])
                    }
                  >
                    {saving.google ? "Saving…" : "Save Google Calendar keys"}
                  </button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </Card>
    </div>
  );
}
